import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Avatar,
  Box,
  Button,
  Chip,
  Container,
  Stack,
  Typography,
} from "@mui/material";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import EditProfile from "../components/Profile/EditProfile";
import { authFetch } from "../utils/AuthFetch";

export default function Profile() {
  const navigate = useNavigate();
  const { user: authUser, updateUser, logout, authLoading } = useAuth();

  const [user, setUser] = useState(null);
  const [banner, setBanner] = useState({
    type: "",
    message: "",
  });
  const [loading, setLoading] = useState(true);
  const [activeSection, setActiveSection] = useState("personal");
  const [editMode, setEditMode] = useState(null);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    if (authLoading) return;

    if (!authUser) {
      navigate("/login");
      return;
    }

    if (authUser.role === "admin" || authUser.role === "staff") {
      navigate("/admin/dashboard/staff");
      return;
    }

    const fetchProfile = async () => {
      try {
        setLoading(true);

        const response = await authFetch("/api/gym/profile/", {
          method: "GET",
        });

        const data = await response.json();

        if (!response.ok) {
          setBanner({
            type: "error",
            message: data.error || "No se pudo cargar tu perfil",
          });

          if (response.status === 401) {
            logout();
            navigate("/login");
          }

          return;
        }

        setUser(data);
      } catch {
        setBanner({
          type: "error",
          message: "Ocurrió un error al conectar con el servidor",
        });
      } finally {
        setLoading(false);
      }
    };

    fetchProfile();
  }, [authLoading, authUser, navigate, logout]);

  const handleProfileUpdated = (updatedUser, message) => {
    setUser(updatedUser);
    updateUser(updatedUser);
    setEditMode(null);
    setBanner({
      type: "success",
      message: message || "Perfil actualizado correctamente",
    });
  };

  const handleProfileError = (message) => {
    setBanner({
      type: "error",
      message,
    });
  };

  const handleDownloadQR = async () => {
    try {
      const response = await authFetch("/api/gym/profile/qr/download/", {
        method: "GET",
      });

      if (!response.ok) {
        if (response.status === 401) {
          logout();
          navigate("/login");
          return;
        }

        throw new Error();
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = "mi_qr_novaxfit.png";
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch {
      setBanner({
        type: "error",
        message: "No se pudo descargar tu código QR",
      });
    }
  };

  const profilePhoto = user?.profile?.profile_photo || "";
  const fullName = `${user?.first_name || ""} ${user?.last_name || ""}`.trim();
  const initial = (fullName || user?.email || "U").charAt(0).toUpperCase();

  const membership = useMemo(() => {
    const activeMembership = user?.active_membership;
    const fallbackMembership = user?.membership;

    if (
      activeMembership &&
      typeof activeMembership === "object" &&
      Object.keys(activeMembership).length > 0
    ) {
      return activeMembership;
    }

    if (
      fallbackMembership &&
      typeof fallbackMembership === "object" &&
      Object.keys(fallbackMembership).length > 0
    ) {
      return fallbackMembership;
    }

    return null;
  }, [user]);

  const hasMembership = Boolean(membership);

  const membershipName =
    membership?.plan_name ||
    membership?.plan?.name ||
    membership?.name ||
    "Sin membresía activa";

  const membershipIsActive =
    membership?.is_currently_active === true || membership?.is_active === true;

  const membershipStatus = hasMembership
    ? membershipIsActive
      ? "Activa"
      : "Inactiva"
    : "Sin membresía";

  const membershipStatusColor = hasMembership
    ? membershipIsActive
      ? "#16a34a"
      : "#e50914"
    : "rgba(17,17,17,0.08)";

  const membershipUsageType =
    membership?.plan_usage_type === "visits"
      ? "Por visitas"
      : membership?.plan_usage_type === "calendar"
      ? "Por tiempo"
      : membership?.plan?.usage_type === "visits"
      ? "Por visitas"
      : membership?.plan?.usage_type === "calendar"
      ? "Por tiempo"
      : "No disponible";

  const hasBillingCompleted = Boolean(
    user?.profile?.document_type?.trim() && user?.profile?.document_number?.trim()
  );

  const sidebarItems = [
    { key: "personal", label: "Info personal" },
    { key: "membership", label: "Membresía" },
    { key: "billing", label: "Facturación" },
  ];

  const startEditPersonal = () => {
    setEditMode("personal");
    setBanner({ type: "", message: "" });
    setActiveSection("personal");
  };

  const startEditPhoto = () => {
    setEditMode("photo");
    setBanner({ type: "", message: "" });
    setActiveSection("personal");
  };

  const startEditBilling = () => {
    setEditMode("billing");
    setBanner({ type: "", message: "" });
    setActiveSection("billing");
  };

  const handleCancelEdit = () => {
    setEditMode(null);
    setBanner({ type: "", message: "" });
  };

  return (
    <Box
      sx={{
        width: "100%",
        minHeight: "100vh",
        bgcolor: "#ffffff",
        py: { xs: 5, md: 7 },
      }}
    >
      <Container
        maxWidth={false}
        sx={{ width: { xs: "92%", md: "82%", lg: "78%" }, mx: "auto" }}
      >
        <Box
          sx={{
            borderRadius: { xs: "24px", md: "28px" },
            border: "1px solid rgba(17,17,17,0.08)",
            bgcolor: "#ffffff",
            boxShadow: "0 24px 70px rgba(0,0,0,0.08)",
            overflow: "hidden",
          }}
        >
          <Box
            sx={{
              px: { xs: 2.4, sm: 3, md: 4 },
              pt: { xs: 2.8, md: 3.4 },
              pb: { xs: 2.2, md: 2.8 },
              borderBottom: "1px solid rgba(17,17,17,0.06)",
              background:
                "linear-gradient(180deg, rgba(17,17,17,0.03) 0%, rgba(17,17,17,0.00) 100%)",
            }}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: { xs: "flex-start", md: "center" },
                justifyContent: "space-between",
                flexDirection: { xs: "column", md: "row" },
                gap: 2,
              }}
            >
              <Box>
                <Typography
                  sx={{
                    color: "#111111",
                    fontWeight: 900,
                    lineHeight: 1.02,
                    letterSpacing: -1.1,
                    fontSize: {
                      xs: "clamp(30px, 8vw, 40px)",
                      md: "clamp(36px, 3vw, 52px)",
                    },
                    mb: 1,
                  }}
                >
                  Mi perfil NovaXFit
                </Typography>

                <Typography
                  sx={{
                    color: "rgba(17,17,17,0.72)",
                    lineHeight: 1.8,
                    fontSize: { xs: 15.2, md: 16.2 },
                    fontWeight: 500,
                    maxWidth: 760,
                  }}
                >
                  Gestiona tu información personal, tu código QR, tu membresía y tus datos de facturación.
                </Typography>
              </Box>

              {!loading && user && (
                <Box
                  sx={{
                    display: "flex",
                    flexWrap: "wrap",
                    gap: 1.2,
                  }}
                >
                  <Button
                    onClick={handleDownloadQR}
                    variant="contained"
                    sx={{
                      textTransform: "none",
                      fontWeight: 800,
                      borderRadius: "14px",
                      px: 2.6,
                      py: 1,
                      color: "#ffffff",
                      background:
                        "linear-gradient(135deg, #111111 0%, #2b2b2b 45%, #e50914 100%)",
                      boxShadow: "0 18px 50px rgba(0,0,0,0.18)",
                      "&:hover": {
                        filter: "brightness(1.05)",
                      },
                    }}
                  >
                    Descargar QR
                  </Button>
                </Box>
              )}
            </Box>
          </Box>

          <Box sx={{ p: { xs: 2.4, sm: 3, md: 4 } }}>
            <Stack spacing={3}>
              {banner.message && (
                <Alert severity={banner.type} sx={{ borderRadius: "14px" }}>
                  {banner.message}
                </Alert>
              )}

              {loading ? (
                <Typography
                  sx={{
                    color: "rgba(17,17,17,0.72)",
                    fontSize: 16,
                    fontWeight: 600,
                  }}
                >
                  Cargando perfil...
                </Typography>
              ) : user ? (
                <Box
                  sx={{
                    display: "grid",
                    gridTemplateColumns: { xs: "1fr", lg: "280px 1fr" },
                    gap: 3,
                    alignItems: "start",
                  }}
                >
                  <Box
                    sx={{
                      position: { lg: "sticky" },
                      top: { lg: 24 },
                      display: "flex",
                      flexDirection: "column",
                      gap: 2,
                    }}
                  >
                    <Box
                      sx={{
                        borderRadius: "22px",
                        border: "1px solid rgba(17,17,17,0.08)",
                        p: 3,
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        textAlign: "center",
                        gap: 1.4,
                        background:
                          "linear-gradient(180deg, rgba(17,17,17,0.03) 0%, rgba(17,17,17,0.00) 100%)",
                      }}
                    >
                      <Box sx={{ position: "relative" }}>
                        <Avatar
                          src={profilePhoto || undefined}
                          sx={{
                            width: 116,
                            height: 116,
                            fontSize: 36,
                            fontWeight: 800,
                            bgcolor: "#111111",
                            color: "#ffffff",
                            boxShadow: "0 16px 36px rgba(0,0,0,0.15)",
                          }}
                        >
                          {initial}
                        </Avatar>

                        <Button
                          onClick={startEditPhoto}
                          sx={{
                            minWidth: 0,
                            width: 36,
                            height: 36,
                            borderRadius: "999px",
                            p: 0,
                            position: "absolute",
                            right: -2,
                            bottom: -2,
                            bgcolor: "#ffffff",
                            color: "#111111",
                            border: "1px solid rgba(17,17,17,0.12)",
                            boxShadow: "0 12px 30px rgba(0,0,0,0.16)",
                            "&:hover": {
                              bgcolor: "#ffffff",
                              color: "#e50914",
                              borderColor: "#e50914",
                            },
                          }}
                        >
                          <EditOutlinedIcon sx={{ fontSize: 18 }} />
                        </Button>
                      </Box>

                      <Typography
                        sx={{
                          color: "#111111",
                          fontWeight: 800,
                          fontSize: 22,
                          lineHeight: 1.15,
                        }}
                      >
                        {fullName || "Usuario"}
                      </Typography>

                      <Typography
                        sx={{
                          color: "#e50914",
                          fontWeight: 800,
                          fontSize: 14.5,
                          textTransform: "capitalize",
                        }}
                      >
                        {user.role}
                      </Typography>

                      <Chip
                        label={membershipStatus}
                        sx={{
                          mt: 0.4,
                          bgcolor: membershipStatusColor,
                          color: hasMembership ? "#ffffff" : "#111111",
                          fontWeight: 800,
                          borderRadius: "999px",
                        }}
                      />
                    </Box>

                    <Box
                      sx={{
                        borderRadius: "22px",
                        border: "1px solid rgba(17,17,17,0.08)",
                        p: 1.2,
                        bgcolor: "#ffffff",
                      }}
                    >
                      <Stack spacing={0.8}>
                        {sidebarItems.map((item) => {
                          const selected = activeSection === item.key;

                          return (
                            <Button
                              key={item.key}
                              onClick={() => {
                                setActiveSection(item.key);
                                setEditMode(null);
                              }}
                              sx={{
                                justifyContent: "flex-start",
                                textTransform: "none",
                                borderRadius: "14px",
                                px: 1.6,
                                py: 1.2,
                                fontWeight: 800,
                                fontSize: 14.4,
                                color: selected ? "#ffffff" : "#111111",
                                bgcolor: selected ? "#111111" : "transparent",
                                border: selected
                                  ? "1px solid #111111"
                                  : "1px solid transparent",
                                "&:hover": {
                                  bgcolor: selected ? "#111111" : "rgba(17,17,17,0.05)",
                                },
                              }}
                            >
                              {item.label}
                            </Button>
                          );
                        })}
                      </Stack>
                    </Box>
                  </Box>

                  <Box sx={{ minWidth: 0 }}>
                    {activeSection === "personal" && (
                      <Stack spacing={2}>
                        <Box
                          sx={{
                            borderRadius: "22px",
                            border: "1px solid rgba(17,17,17,0.08)",
                            p: { xs: 2.2, md: 2.6 },
                            bgcolor: "#ffffff",
                          }}
                        >
                          <Box
                            sx={{
                              display: "flex",
                              justifyContent: "space-between",
                              alignItems: { xs: "flex-start", md: "center" },
                              flexDirection: { xs: "column", md: "row" },
                              gap: 1.5,
                            }}
                          >
                            <Box>
                              <Typography
                                sx={{
                                  color: "#111111",
                                  fontWeight: 900,
                                  fontSize: { xs: 24, md: 28 },
                                  letterSpacing: -0.5,
                                  mb: 0.7,
                                }}
                              >
                                Información personal
                              </Typography>

                              <Typography
                                sx={{
                                  color: "rgba(17,17,17,0.66)",
                                  fontSize: 15,
                                  lineHeight: 1.7,
                                }}
                              >
                                Revisa y actualiza tus datos personales principales.
                              </Typography>
                            </Box>

                            {editMode !== "personal" && editMode !== "photo" && (
                              <Button
                                onClick={startEditPersonal}
                                variant="contained"
                                sx={{
                                  textTransform: "none",
                                  fontWeight: 800,
                                  borderRadius: "14px",
                                  px: 2.4,
                                  py: 1,
                                  color: "#ffffff",
                                  background:
                                    "linear-gradient(135deg, #5f0008 0%, #a80d19 35%, #df2632 62%, #161616 100%)",
                                  boxShadow: "0 18px 50px rgba(229,9,20,0.22)",
                                  "&:hover": {
                                    filter: "brightness(1.05)",
                                  },
                                }}
                              >
                                Editar info personal
                              </Button>
                            )}
                          </Box>
                        </Box>

                        {editMode === "personal" || editMode === "photo" ? (
                          <Box
                            sx={{
                              borderRadius: "22px",
                              border: "1px solid rgba(17,17,17,0.08)",
                              p: { xs: 2.2, md: 2.6 },
                              bgcolor: "#ffffff",
                            }}
                          >
                            <EditProfile
                              user={user}
                              mode={editMode}
                              onCancel={handleCancelEdit}
                              onUpdated={handleProfileUpdated}
                              onError={handleProfileError}
                            />
                          </Box>
                        ) : (
                          <Box
                            sx={{
                              display: "grid",
                              gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
                              gap: 2,
                            }}
                          >
                            <InfoCard label="Nombres" value={user.first_name || "No disponible"} />
                            <InfoCard label="Apellidos" value={user.last_name || "No disponible"} />
                            <InfoCard label="Correo" value={user.email || "No disponible"} />
                            <InfoCard label="Teléfono" value={user.phone || "No disponible"} />
                            <InfoCard label="Dirección" value={user?.profile?.address || "No registrada"} />
                            <InfoCard label="Estado de cuenta" value={user?.is_active ? "Activa" : "Inactiva"} />
                          </Box>
                        )}
                      </Stack>
                    )}

                    {activeSection === "membership" && (
                      <Stack spacing={2}>
                        <Box
                          sx={{
                            borderRadius: "22px",
                            border: "1px solid rgba(17,17,17,0.08)",
                            p: { xs: 2.2, md: 2.6 },
                            bgcolor: "#ffffff",
                          }}
                        >
                          <Typography
                            sx={{
                              color: "#111111",
                              fontWeight: 900,
                              fontSize: { xs: 24, md: 28 },
                              letterSpacing: -0.5,
                              mb: 0.7,
                            }}
                          >
                            Membresía
                          </Typography>

                          <Typography
                            sx={{
                              color: "rgba(17,17,17,0.66)",
                              fontSize: 15,
                              lineHeight: 1.7,
                            }}
                          >
                            Consulta el plan que tienes asignado y el estado actual de tu membresía.
                          </Typography>
                        </Box>

                        <Box
                          sx={{
                            borderRadius: "24px",
                            border: "1px solid rgba(17,17,17,0.08)",
                            p: { xs: 2.2, md: 2.8 },
                            bgcolor: hasMembership
                              ? membershipIsActive
                                ? "rgba(22,163,74,0.05)"
                                : "rgba(229,9,20,0.04)"
                              : "rgba(17,17,17,0.03)",
                          }}
                        >
                          <Stack
                            direction={{ xs: "column", md: "row" }}
                            alignItems={{ xs: "flex-start", md: "center" }}
                            justifyContent="space-between"
                            spacing={1.4}
                          >
                            <Box>
                              <Typography
                                sx={{
                                  color: "rgba(17,17,17,0.52)",
                                  fontSize: 12.5,
                                  fontWeight: 800,
                                  textTransform: "uppercase",
                                  letterSpacing: 0.6,
                                  mb: 0.8,
                                }}
                              >
                                Estado general
                              </Typography>

                              <Typography
                                sx={{
                                  color: "#111111",
                                  fontSize: { xs: 22, md: 26 },
                                  fontWeight: 900,
                                  lineHeight: 1.15,
                                  mb: 0.6,
                                }}
                              >
                                {hasMembership
                                  ? membershipIsActive
                                    ? "Tu membresía está activa"
                                    : "Tu membresía está inactiva"
                                  : "No tienes membresía registrada"}
                              </Typography>

                              <Typography
                                sx={{
                                  color: "rgba(17,17,17,0.66)",
                                  fontSize: 14.8,
                                  lineHeight: 1.7,
                                  maxWidth: 700,
                                }}
                              >
                                {hasMembership
                                  ? "Aquí puedes revisar la información principal de tu plan actual."
                                  : "Cuando tengas una membresía asignada aparecerá en esta sección."}
                              </Typography>
                            </Box>

                            <Chip
                              label={membershipStatus}
                              sx={{
                                bgcolor: membershipStatusColor,
                                color: hasMembership ? "#ffffff" : "#111111",
                                fontWeight: 900,
                                borderRadius: "999px",
                                px: 1.1,
                                height: 38,
                              }}
                            />
                          </Stack>
                        </Box>

                        <Box
                          sx={{
                            display: "grid",
                            gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
                            gap: 2,
                          }}
                        >
                          <InfoCard label="Plan actual" value={membershipName} />
                          <InfoCard label="Estado de membresía" value={membershipStatus} />
                          <InfoCard label="Tipo de uso" value={hasMembership ? membershipUsageType : "No disponible"} />
                          <InfoCard label="Nota" value={hasMembership ? membership?.notes || "Sin nota" : "Sin nota"} />
                        </Box>
                      </Stack>
                    )}

                    {activeSection === "billing" && (
                      <Stack spacing={2}>
                        <Box
                          sx={{
                            borderRadius: "22px",
                            border: "1px solid rgba(17,17,17,0.08)",
                            p: { xs: 2.2, md: 2.6 },
                            bgcolor: "#ffffff",
                          }}
                        >
                          <Box
                            sx={{
                              display: "flex",
                              justifyContent: "space-between",
                              alignItems: { xs: "flex-start", md: "center" },
                              flexDirection: { xs: "column", md: "row" },
                              gap: 1.5,
                            }}
                          >
                            <Box>
                              <Typography
                                sx={{
                                  color: "#111111",
                                  fontWeight: 900,
                                  fontSize: { xs: 24, md: 28 },
                                  letterSpacing: -0.5,
                                  mb: 0.7,
                                }}
                              >
                                Facturación
                              </Typography>

                              <Typography
                                sx={{
                                  color: "rgba(17,17,17,0.66)",
                                  fontSize: 15,
                                  lineHeight: 1.7,
                                }}
                              >
                                Aquí se registran tus datos fiscales para facturas y comprobantes.
                              </Typography>
                            </Box>

                            {!hasBillingCompleted && editMode !== "billing" && (
                              <Button
                                onClick={startEditBilling}
                                variant="contained"
                                sx={{
                                  textTransform: "none",
                                  fontWeight: 800,
                                  borderRadius: "14px",
                                  px: 2.4,
                                  py: 1,
                                  color: "#ffffff",
                                  background:
                                    "linear-gradient(135deg, #5f0008 0%, #a80d19 35%, #df2632 62%, #161616 100%)",
                                  boxShadow: "0 18px 50px rgba(229,9,20,0.22)",
                                  "&:hover": {
                                    filter: "brightness(1.05)",
                                  },
                                }}
                              >
                                Completar facturación
                              </Button>
                            )}
                          </Box>
                        </Box>

                        {editMode === "billing" ? (
                          <Box
                            sx={{
                              borderRadius: "22px",
                              border: "1px solid rgba(17,17,17,0.08)",
                              p: { xs: 2.2, md: 2.6 },
                              bgcolor: "#ffffff",
                            }}
                          >
                            <EditProfile
                              user={user}
                              mode="billing"
                              onCancel={handleCancelEdit}
                              onUpdated={handleProfileUpdated}
                              onError={handleProfileError}
                            />
                          </Box>
                        ) : (
                          <>
                            <Box
                              sx={{
                                display: "grid",
                                gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
                                gap: 2,
                              }}
                            >
                              <InfoCard
                                label="Tipo de documento"
                                value={user?.profile?.document_type || "No registrado"}
                              />
                              <InfoCard
                                label="Número de documento"
                                value={user?.profile?.document_number || "No registrado"}
                              />
                            </Box>

                            {hasBillingCompleted && (
                              <Box
                                sx={{
                                  borderRadius: "20px",
                                  border: "1px solid rgba(17,17,17,0.08)",
                                  p: 2.2,
                                  bgcolor: "rgba(17,17,17,0.02)",
                                }}
                              >
                                <Typography
                                  sx={{
                                    color: "#111111",
                                    fontWeight: 800,
                                    fontSize: 15.5,
                                    mb: 0.6,
                                  }}
                                >
                                  Datos fiscales bloqueados
                                </Typography>

                                <Typography
                                  sx={{
                                    color: "rgba(17,17,17,0.66)",
                                    fontSize: 14.5,
                                    lineHeight: 1.7,
                                  }}
                                >
                                  Tus datos de facturación ya fueron registrados y no se pueden modificar desde aquí.
                                </Typography>
                              </Box>
                            )}
                          </>
                        )}
                      </Stack>
                    )}
                  </Box>
                </Box>
              ) : null}
            </Stack>
          </Box>
        </Box>
      </Container>
    </Box>
  );
}

function InfoCard({ label, value }) {
  return (
    <Box
      sx={{
        borderRadius: "20px",
        border: "1px solid rgba(17,17,17,0.08)",
        p: 2.2,
        bgcolor: "#ffffff",
      }}
    >
      <Typography
        sx={{
          color: "rgba(17,17,17,0.52)",
          fontSize: 12.5,
          fontWeight: 800,
          textTransform: "uppercase",
          letterSpacing: 0.6,
          mb: 0.8,
        }}
      >
        {label}
      </Typography>

      <Typography
        sx={{
          color: "#111111",
          fontSize: 16.5,
          fontWeight: 800,
          lineHeight: 1.45,
          wordBreak: "break-word",
        }}
      >
        {value || "No disponible"}
      </Typography>
    </Box>
  );
}